// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface SendOTPResponse {
  message: string;
  identifier: string;
  authType: string;
  expiresIn: string; // e.g., "10 minutes"
  otpSent: boolean;
}

export interface VerifyOTPResponse {
  message: string;
  token?: string;
  user?: {
    id: string;
    phone: string;
    name?: string;
  };
}

// API User Response (from backend)
export interface ApiUser {
  user_id: number;
  phone: string;
  email: string | null;
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  name?: string | null;
  roles: string[];
  created_at: string;
  updated_at: string;
}

// Frontend User (mapped for UI)
export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'PENDING';
  lastLogin: string;
  avatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetAllUsersResponse {
  users: ApiUser[];
}

// API Restaurant Response (from backend)
export interface ApiRestaurant {
  restaurant_id?: number;
  id?: number;
  name: string;
  google_place_id?: string;
  googlePlaceId?: string;
  brandName?: string | null;
  description?: string | null;
  cuisineType?: string[] | null;
  restaurantCategory?: string[] | null;
  owner_id?: number;
  owner?: string;
  owner_name?: string;
  status?: 'active' | 'pending' | 'inactive' | 'ACTIVE' | 'PENDING' | 'INACTIVE';
  city?: string;
  location?: string;
  address?: string;
  locations?: Array<{
    id?: number;
    city?: string;
    address?: string;
    [key: string]: any;
  }>;
  menu_items?: number;
  menuItems?: number;
  active_deals?: number;
  activeDeals?: number;
  created_at?: string;
  createdAt?: string;
  created?: string;
  updated_at?: string;
  updatedAt?: string;
  verified?: boolean;
  stripeAccountId?: string | null;
  stripeConnectAccount?: any;
  [key: string]: any; // Allow for additional fields
}

// Frontend Restaurant (mapped for UI)
export interface Restaurant {
  id: string;
  name: string;
  googlePlaceId: string;
  owner: string;
  status: 'active' | 'pending' | 'inactive';
  city: string;
  menuItems: number;
  activeDeals: number;
  created: string;
  verified?: boolean;
}

export interface GetAllRestaurantsResponse {
  restaurants?: ApiRestaurant[];
  data?: ApiRestaurant[];
  [key: string]: any; // Allow for different response structures
}

// API Deal Response (from backend)
export interface ApiDeal {
  id: number;
  title: string;
  description: string | null;
  restaurant_id: number;
  menu_item_id?: number | null;
  category_id?: number | null;
  user_id?: number | null;
  day_of_week?: string | null;
  date?: string | null;
  time_start?: string | null;
  time_end?: string | null;
  min_order_amount?: number | null;
  type: 'PERCENTAGE' | 'FLAT' | 'BOGO';
  discount_percent?: number | null;
  max_discount_amount?: number | null;
  discount_amount?: number | null;
  bogo_buy_qty?: number | null;
  bogo_get_qty?: number | null;
  price?: number | null;
  is_active: boolean;
  priority: number;
  promo_code?: string | null;
  createdAt: string;
  updatedAt: string;
  restaurant?: any | null;
  [key: string]: any; // Allow for additional fields
}

// Frontend Deal (mapped for UI)
export interface Deal {
  id: string;
  name: string;
  type: string;
  discountLabel: string;
  status: 'active' | 'scheduled' | 'paused' | 'expired';
  activeDates: string;
  usage: number;
  revenueImpact: string;
  isActive: boolean;
}

export interface GetAllDealsResponse {
  success: boolean;
  deals?: ApiDeal[];
  data?: ApiDeal[];
  [key: string]: any; // Allow for different response structures
}

// API Document Response (from backend)
export interface ApiDocument {
  id: number;
  restaurant?: {
    id?: number;
    name?: string;
    restaurant_id?: number;
  };
  restaurant_id?: number;
  documentType?: {
    id?: number;
    name?: string;
    type?: string;
  };
  document_type_id?: number;
  fileName: string;
  fileUrl: string;
  originalFileName?: string;
  fileSize?: number;
  mimeType?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_INFO' | 'pending' | 'approved' | 'rejected' | 'needs-info';
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  expiryDate?: string;
  description?: string;
  tags?: string;
  isActive?: boolean;
  rejectionReason?: string;
  lastReminderSent?: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: any; // Allow for additional fields
}

// Frontend Document (mapped for UI)
export interface Document {
  id: string;
  rawId?: number; // numeric id for API calls
  restaurant: string;
  documentType: string;
  status: 'pending' | 'approved' | 'rejected' | 'needs-info';
  uploadDate: string;
  reviewer: string;
  hasInfo?: boolean;
  fileUrl?: string; // for viewing in modal
}

export interface GetAllDocumentsResponse {
  documents?: ApiDocument[];
  data?: ApiDocument[];
  success?: boolean;
  [key: string]: any; // Allow for different response structures
}

// Update document status (admin only)
export type DocumentStatusUpdate = 'pending' | 'approved' | 'rejected' | 'expired';

export interface UpdateDocumentStatusRequest {
  status: DocumentStatusUpdate;
  reviewNotes?: string;
  rejectionReason?: string;
}

export interface UpdateDocumentStatusResponse {
  success: boolean;
  message?: string;
  document?: ApiDocument;
  [key: string]: any;
}

// Create Restaurant With User request payload
export interface CreateRestaurantWithUserRequest {
  user: { phone?: string; email?: string; name?: string };
  restaurant: {
    googlePlaceId: string;
    name: string;
    brandName?: string;
    description?: string;
    cuisineType?: string[];
  };
  location?: {
    address: string;
    city: string;
    state: string;
    zip: string;
    phone?: string;
    timezone?: string;
    lat?: number;
    lng?: number;
  };
}

export interface LogoutResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/** Home — food categories (admin). `icon` is the image URL set by the server after upload. */
export interface ApiFoodCategory {
  id: number;
  name: string;
  icon?: string | null;
  type?: string | null;
  image?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  is_veg?: boolean | null;
  is_active?: boolean;
  priority?: number | null;
  [key: string]: unknown;
}

export interface FoodCategoryItem {
  id: number;
  name: string;
  /** Image URL returned by API (S3); not edited from the admin form. */
  icon: string;
  type: string;
  /** Resolved URL for preview/cards: `image` fields, or `icon` when it looks like a URL. */
  imageUrl: string;
  isVeg: boolean;
  isActive: boolean;
  priority: number | null;
}

/** Home — banners (admin) */
export interface ApiBanner {
  id: number;
  title: string;
  image?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  cta?: string | null;
  is_active?: boolean;
  priority?: number | null;
  [key: string]: unknown;
}

export interface BannerItem {
  id: number;
  title: string;
  imageUrl: string;
  cta: string;
  isActive: boolean;
  priority: number | null;
}

// API Error class
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Helper function to get auth token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

type AuthFailureHandler = (reason: { status: number; message?: string; url?: string }) => void;
let authFailureHandler: AuthFailureHandler | null = null;

function isPublicAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  // These endpoints can legitimately return 401/403 without implying an expired session token.
  return (
    url.includes('/users/sendOtp') ||
    url.includes('/users/verifyOtp') ||
    url.includes('/auth/resend-otp')
  );
}

function maybeHandleAuthFailure(response: Response, message?: string, requestUrl?: string) {
  const status = response.status;
  if (status !== 401 && status !== 403) return;

  // Only force-logout if we *actually* have a session token to invalidate.
  const token = getAuthToken();
  if (!token) return;

  // Use requestUrl if provided, otherwise fall back to response.url
  const url = requestUrl || response.url || '';

  // Avoid logging out on OTP verification failures.
  if (isPublicAuthEndpoint(url)) return;

  const msg = (message || '').toLowerCase();
  const looksLikeExpiredToken =
    msg.includes('invalid or expired token') ||
    msg.includes('token expired') ||
    msg.includes('jwt expired') ||
    msg.includes('invalid token') ||
    msg.includes('unauthorized');

  // Only trigger logout if:
  // 1. The message explicitly indicates token expiration, OR
  // 2. It's a 401 status (403 might be permission denied, not token expired)
  if (!looksLikeExpiredToken && status !== 401) return;

  authFailureHandler?.({ status, message, url });
}

// Helper function to build headers with optional authentication
function buildHeaders(includeAuth: boolean = false): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token.trim()}`;
    }
  }

  return headers;
}

/** Authorization only — use with multipart FormData (do not set Content-Type). */
function buildAuthHeadersMultipart(): HeadersInit {
  const headers: HeadersInit = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token.trim()}`;
  }
  return headers;
}

function normalizeHomeList<T>(data: unknown, keys: string[]): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const k of keys) {
      const v = obj[k];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

/** Resolve relative image URLs from the API host (e.g. `/uploads/...`). */
export function resolvePublicAssetUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response, requestUrl?: string): Promise<T> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');
  
  if (!response.ok) {
    const errorData = isJson ? await response.json() : { message: response.statusText };
    const message = errorData?.message || errorData?.error || response.statusText || 'An error occurred';
    maybeHandleAuthFailure(response, message, requestUrl);
    throw new ApiError(response.status, message);
  }

  const data = isJson ? await response.json() : await response.text();
  return data as T;
}

// Helper function to format last login time
function formatLastLogin(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} ${Math.floor(diffDays / 7) === 1 ? 'week' : 'weeks'} ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} ${Math.floor(diffDays / 30) === 1 ? 'month' : 'months'} ago`;
  return `${Math.floor(diffDays / 365)} ${Math.floor(diffDays / 365) === 1 ? 'year' : 'years'} ago`;
}

// API Service
export const apiService = {
  /**
   * Register a handler that will be invoked when an authenticated request fails
   * due to an invalid/expired token (e.g. 401/403).
   */
  setAuthFailureHandler(handler: AuthFailureHandler | null) {
    authFailureHandler = handler;
  },

  /**
   * Send OTP to phone number
   */
  async sendOTP(phone: string): Promise<SendOTPResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/users/sendOtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: phone.startsWith('+') ? phone : `+${phone}` }),
      });

      return await handleResponse<SendOTPResponse>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Verify OTP
   */
  async verifyOTP(phone: string, otp: string): Promise<VerifyOTPResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/users/verifyOtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phone: phone,
          otp: otp 
        }),
      });

      return await handleResponse<VerifyOTPResponse>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Resend OTP
   */
  async resendOTP(phone: string): Promise<SendOTPResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: phone.startsWith('+') ? phone : `+${phone}` }),
      });

      return await handleResponse<SendOTPResponse>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Get all users
   */
  async getAllUsers(signal?: AbortSignal): Promise<GetAllUsersResponse> {
    try {
      const url = `${API_BASE_URL}/users/all`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });

      return await handleResponse<GetAllUsersResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Helper function to map API user to frontend user format
   */
  mapApiUserToUser(apiUser: ApiUser): User {
    // Normalise roles from various possible backend shapes
    const rawRoles: any =
      (apiUser as any).roles !== undefined
        ? (apiUser as any).roles
        : (apiUser as any).role;

    let rolesArray: string[] = [];
    if (Array.isArray(rawRoles)) {
      rolesArray = rawRoles.map((r) => String(r));
    } else if (typeof rawRoles === 'string') {
      rolesArray = [rawRoles];
    }

    const formatRoleLabel = (role: string) => {
      const cleaned = role.replace(/_/g, ' ').replace(/-/g, ' ').toLowerCase();
      return cleaned
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    };

    // Choose a primary role with simple priority (Admin/Owner over Customer)
    const priorityOrder = ['SUPER_ADMIN', 'ADMIN', 'OWNER', 'RESTAURANT_OWNER', 'MANAGER', 'STAFF', 'CUSTOMER'];
    const upperRoles = rolesArray.map((r) => r.toUpperCase());
    let chosenRole: string | undefined;

    for (const p of priorityOrder) {
      const idx = upperRoles.indexOf(p);
      if (idx !== -1) {
        chosenRole = rolesArray[idx];
        break;
      }
    }

    if (!chosenRole && rolesArray.length > 0) {
      chosenRole = rolesArray[0];
    }

    const primaryRole = chosenRole ? formatRoleLabel(chosenRole) : 'Customer';

    // Format name (use name if available, otherwise use phone or default)
    const displayName = apiUser.name || `User ${apiUser.user_id}` || 'Unknown User';

    // Format email (use email if available, otherwise empty string)
    const displayEmail = apiUser.email || '';

    // Calculate last login from updated_at (or show "Never" if same as created_at)
    const lastLogin = apiUser.updated_at !== apiUser.created_at
      ? formatLastLogin(apiUser.updated_at)
      : 'Never';

    return {
      id: `#${apiUser.user_id}`,
      name: displayName,
      email: displayEmail,
      phone: apiUser.phone,
      role: primaryRole,
      status: apiUser.status as 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'PENDING',
      lastLogin: lastLogin,
      createdAt: apiUser.created_at,
      updatedAt: apiUser.updated_at,
    };
  },

  /**
   * Get all restaurants
   */
  async getAllRestaurants(signal?: AbortSignal): Promise<GetAllRestaurantsResponse> {
    try {
      const url = `${API_BASE_URL}/restaurants`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });

      return await handleResponse<GetAllRestaurantsResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Helper function to map API restaurant to frontend restaurant format
   */
  mapApiRestaurantToRestaurant(apiRestaurant: ApiRestaurant): Restaurant {
    // Get restaurant ID
    const restaurantId = apiRestaurant.restaurant_id || apiRestaurant.id || 0;
    
    // Get Google Place ID
    const googlePlaceId = apiRestaurant.google_place_id || apiRestaurant.googlePlaceId || 'N/A';
    
    // Get owner name (may not be in response, default to Unknown)
    const ownerName = apiRestaurant.owner_name || apiRestaurant.owner || 'Unknown Owner';
    
    // Normalize status to lowercase (default to 'pending' if not provided)
    const status = apiRestaurant.status 
      ? (apiRestaurant.status.toLowerCase() as 'active' | 'pending' | 'inactive')
      : 'pending';
    
    // Get city from various possible sources
    let city = apiRestaurant.city || apiRestaurant.location || apiRestaurant.address;
    
    // If city not found, try to get it from locations array
    if (!city && apiRestaurant.locations && apiRestaurant.locations.length > 0) {
      const firstLocation = apiRestaurant.locations[0];
      city = firstLocation.city || firstLocation.address || 'Unknown';
    }
    
    // Default to 'Unknown' if still no city found
    if (!city) {
      city = 'Unknown';
    }
    
    // Get menu items count (default to 0 if not provided)
    const menuItems = apiRestaurant.menu_items || apiRestaurant.menuItems || 0;
    
    // Get active deals count (default to 0 if not provided)
    const activeDeals = apiRestaurant.active_deals || apiRestaurant.activeDeals || 0;
    
    // Format created date (handle both camelCase and snake_case)
    const createdDate = apiRestaurant.createdAt || apiRestaurant.created_at || apiRestaurant.created;
    const created = createdDate 
      ? new Date(createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : 'Unknown';

    return {
      id: `REST-${String(restaurantId).padStart(3, '0')}`,
      name: apiRestaurant.name || 'Unnamed Restaurant',
      googlePlaceId: googlePlaceId,
      owner: ownerName,
      status: status,
      city: city,
      menuItems: menuItems,
      activeDeals: activeDeals,
      created: created,
      verified: apiRestaurant.verified || false,
    };
  },

  /**
   * Get all deals
   */
  async getAllDeals(signal?: AbortSignal): Promise<GetAllDealsResponse> {
    try {
      const url = `${API_BASE_URL}/deals`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });

      return await handleResponse<GetAllDealsResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Create restaurant with owner user and primary location
   */
  async createRestaurantWithUser(payload: CreateRestaurantWithUserRequest): Promise<any> {
    try {
      const url = `${API_BASE_URL}/restaurants/admin/create-with-user`;
      const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders(true),
        body: JSON.stringify(payload),
      });

      return await handleResponse<any>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to create restaurant. Please try again.');
    }
  },

  /**
   * Helper function to map API deal to frontend deal format
   */
  mapApiDealToDeal(apiDeal: ApiDeal): Deal {
    // Format deal ID
    const dealId = `DEAL-${String(apiDeal.id).padStart(3, '0')}`;

    // Map type
    const typeMap: Record<string, string> = {
      'PERCENTAGE': 'Percentage',
      'FLAT': 'Fixed Amount',
      'BOGO': 'BOGO',
    };
    const dealType = typeMap[apiDeal.type] || apiDeal.type;

    // Format discount label
    let discountLabel = '';
    if (apiDeal.type === 'PERCENTAGE' && apiDeal.discount_percent) {
      discountLabel = `${apiDeal.discount_percent}%`;
    } else if (apiDeal.type === 'FLAT' && apiDeal.discount_amount) {
      discountLabel = `$${apiDeal.discount_amount}`;
    } else if (apiDeal.type === 'BOGO' && apiDeal.bogo_buy_qty && apiDeal.bogo_get_qty) {
      discountLabel = `Buy ${apiDeal.bogo_buy_qty} Get ${apiDeal.bogo_get_qty}`;
    } else {
      discountLabel = 'N/A';
    }

    // Determine status based on is_active
    // For now, if active -> 'active', if not -> 'paused'
    // You might want to add more logic based on dates
    const status: 'active' | 'scheduled' | 'paused' | 'expired' = apiDeal.is_active 
      ? 'active' 
      : 'paused';

    // Format active dates
    let activeDates = 'Ongoing';
    if (apiDeal.date) {
      // Single date
      activeDates = new Date(apiDeal.date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } else if (apiDeal.day_of_week) {
      // Day of week
      const dayStr = apiDeal.day_of_week;
      if (apiDeal.time_start && apiDeal.time_end) {
        // Format time (convert 24h to 12h)
        const formatTime = (time: string) => {
          const [hours, minutes] = time.split(':');
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minutes} ${ampm}`;
        };
        activeDates = `${dayStr} (${formatTime(apiDeal.time_start)} - ${formatTime(apiDeal.time_end)})`;
      } else {
        activeDates = dayStr;
      }
    } else if (apiDeal.time_start && apiDeal.time_end) {
      // Just time range
      const formatTime = (time: string) => {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
      };
      activeDates = `${formatTime(apiDeal.time_start)} - ${formatTime(apiDeal.time_end)}`;
    }

    // Usage and revenue impact are not in API, default to 0
    // These could be calculated from order data if available
    const usage = 0;
    const revenueImpact = '$0';

    return {
      id: dealId,
      name: apiDeal.title || 'Unnamed Deal',
      type: dealType,
      discountLabel: discountLabel,
      status: status,
      activeDates: activeDates,
      usage: usage,
      revenueImpact: revenueImpact,
      isActive: apiDeal.is_active,
    };
  },

  /**
   * Get all documents
   */
  async getAllDocuments(signal?: AbortSignal): Promise<GetAllDocumentsResponse> {
    try {
      const url = `${API_BASE_URL}/restaurants/documents/all`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });

      return await handleResponse<GetAllDocumentsResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * Helper function to map API document to frontend document format
   */
  mapApiDocumentToDocument(apiDocument: ApiDocument): Document {
    // Get document ID - format as DOC-XXX (keep raw id for API calls)
    const rawId = typeof apiDocument.id === 'number' ? apiDocument.id : undefined;
    const documentId = apiDocument.id 
      ? `DOC-${String(apiDocument.id).padStart(3, '0')}` 
      : 'DOC-000';
    
    // Get restaurant name from nested restaurant object
    const restaurantName = apiDocument.restaurant?.name || 'Unknown Restaurant';
    
    // Get document type from nested documentType object
    const documentType = apiDocument.documentType?.name || 
      apiDocument.documentType?.type || 
      'Unknown';
    
    // Normalize status - handle both enum and lowercase formats
    let status: 'pending' | 'approved' | 'rejected' | 'needs-info' = 'pending';
    if (apiDocument.status) {
      const statusStr = apiDocument.status.toUpperCase();
      if (statusStr === 'PENDING') status = 'pending';
      else if (statusStr === 'APPROVED') status = 'approved';
      else if (statusStr === 'REJECTED') status = 'rejected';
      else if (statusStr === 'NEEDS_INFO' || statusStr === 'NEEDS-INFO') status = 'needs-info';
      else status = apiDocument.status.toLowerCase().replace('_', '-') as 'pending' | 'approved' | 'rejected' | 'needs-info';
    }
    
    // Get upload/submitted date - use submittedAt, createdAt, or current date
    const uploadDate = apiDocument.submittedAt || 
      apiDocument.createdAt || 
      new Date().toISOString();
    
    // Format date to YYYY-MM-DD
    const formattedDate = uploadDate.includes('T') 
      ? uploadDate.split('T')[0] 
      : uploadDate;
    
    // Get reviewer from reviewedBy field
    const reviewer = apiDocument.reviewedBy || '';
    
    // Check if needs info - status is 'needs-info' or has reviewNotes indicating more info needed
    const hasInfo = status === 'needs-info' || 
      (apiDocument.reviewNotes && apiDocument.reviewNotes.length > 0) ||
      false;

    return {
      id: documentId,
      rawId,
      restaurant: restaurantName,
      documentType: documentType,
      status: status,
      uploadDate: formattedDate,
      reviewer: reviewer,
      hasInfo: hasInfo,
      fileUrl: apiDocument.fileUrl,
    };
  },

  /**
   * Update document status (admin only)
   */
  async updateDocumentStatus(
    documentId: number,
    payload: UpdateDocumentStatusRequest
  ): Promise<UpdateDocumentStatusResponse> {
    try {
      const url = `${API_BASE_URL}/restaurants/documents/${documentId}/status`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: buildHeaders(true),
        body: JSON.stringify(payload),
      });

      return await handleResponse<UpdateDocumentStatusResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to update document status. Please try again.');
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<LogoutResponse> {
    try {
      const url = `${API_BASE_URL}/auth/logout`;
      const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders(true),
      });

      return await handleResponse<LogoutResponse>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  /**
   * List food categories (home). Expects GET /home/categories (array or wrapped).
   */
  async getHomeCategories(signal?: AbortSignal): Promise<ApiFoodCategory[]> {
    try {
      const url = `${API_BASE_URL}/home/categories`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });
      const data = await handleResponse<unknown>(response, url);
      return normalizeHomeList<ApiFoodCategory>(data, ['categories', 'data', 'items']);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  mapApiFoodCategoryToItem(api: ApiFoodCategory): FoodCategoryItem {
    const iconStr = api.icon != null ? String(api.icon).trim() : '';
    const iconLooksLikeAssetUrl =
      /^https?:\/\//i.test(iconStr) || (iconStr.startsWith('/') && iconStr.length > 1);

    const rawFromImageFields =
      api.imageUrl ||
      api.image_url ||
      (typeof api.image === 'string' && (api.image.startsWith('http') || api.image.startsWith('/'))
        ? api.image
        : '') ||
      '';

    const raw = rawFromImageFields || (iconLooksLikeAssetUrl ? iconStr : '');

    return {
      id: api.id,
      name: api.name,
      icon: iconStr,
      type: api.type != null ? String(api.type) : '',
      imageUrl: resolvePublicAssetUrl(raw),
      isVeg: Boolean(api.is_veg),
      isActive: api.is_active !== false,
      priority: api.priority ?? null,
    };
  },

  async createFoodCategory(formData: FormData): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/categories`;
      const response = await fetch(url, {
        method: 'POST',
        headers: buildAuthHeadersMultipart(),
        body: formData,
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to create category.');
    }
  },

  async updateFoodCategory(id: number, formData: FormData): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/categories/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: buildAuthHeadersMultipart(),
        body: formData,
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to update category.');
    }
  },

  async deleteFoodCategory(id: number): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/categories/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: buildHeaders(true),
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to delete category.');
    }
  },

  /**
   * List banners (home). Expects GET /home/banners (array or wrapped).
   */
  async getHomeBanners(signal?: AbortSignal): Promise<ApiBanner[]> {
    try {
      const url = `${API_BASE_URL}/home/banners`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(true),
        signal,
      });
      const data = await handleResponse<unknown>(response, url);
      return normalizeHomeList<ApiBanner>(data, ['banners', 'data', 'items']);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Network error. Please check your connection.');
    }
  },

  mapApiBannerToItem(api: ApiBanner): BannerItem {
    const raw =
      api.imageUrl ||
      api.image_url ||
      (typeof api.image === 'string' ? api.image : '') ||
      '';
    return {
      id: api.id,
      title: api.title,
      imageUrl: resolvePublicAssetUrl(raw),
      cta: api.cta != null ? String(api.cta) : '',
      isActive: api.is_active !== false,
      priority: api.priority ?? null,
    };
  },

  async createBanner(formData: FormData): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/banners`;
      const response = await fetch(url, {
        method: 'POST',
        headers: buildAuthHeadersMultipart(),
        body: formData,
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to create banner.');
    }
  },

  async updateBanner(id: number, formData: FormData): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/banners/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: buildAuthHeadersMultipart(),
        body: formData,
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to update banner.');
    }
  },

  async deleteBanner(id: number): Promise<unknown> {
    try {
      const url = `${API_BASE_URL}/home/banners/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: buildHeaders(true),
      });
      return await handleResponse<unknown>(response, url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Failed to delete banner.');
    }
  },
};

